/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import moment from 'moment'

import { Order } from 'store/types'
import { tsOfficePhone } from 'constants/misc'

interface IProps {
  order: Order | undefined
}

// How long to wait for a fresh checkout session before telling the patient to sit tight. The
// session is created by HstLayout as soon as it sees the order, so this only trips when Stripe
// errors or the order has nothing to charge.
const SESSION_WAIT_MS = 20000

/**
 * Landing spot for the `stripe` step. Paperwork is finished but we have no usable checkout session
 * yet - typically because the previous one expired and a replacement is still being created. Waits
 * for it and forwards, instead of leaving the patient on an unrouted blank page.
 */
const PaymentRedirect = ({ order }: IProps) => {
  const [waitedTooLong, setWaitedTooLong] = useState(false)

  const session = order?.hst?.paymentSession
  const sessionUrl = session?.url
  const sessionUsable =
    !!sessionUrl && !!session?.expires_at && moment().isBefore(moment(session.expires_at * 1000))

  useEffect(() => {
    if (sessionUsable && sessionUrl) {
      window.location.href = sessionUrl
    }
  }, [sessionUsable, sessionUrl])

  useEffect(() => {
    if (sessionUsable) {
      return undefined
    }

    const timer = setTimeout(() => setWaitedTooLong(true), SESSION_WAIT_MS)

    return () => clearTimeout(timer)
  }, [sessionUsable])

  return (
    <div className="container text-center" style={{ maxWidth: 720 }}>
      {waitedTooLong && !sessionUsable ? (
        <>
          <h2 className="mt-4">Thank You — We Received Your Paperwork</h2>
          <p css={{ fontSize: 18 }} className="mt-3">
            We weren't able to open the payment page just now. Your paperwork is saved and nothing
            is lost — we will text you a secure payment link shortly.
          </p>
          <p css={{ fontSize: 18 }}>Questions? Call our office at {tsOfficePhone}.</p>
        </>
      ) : (
        <>
          <h2 className="mt-4">Preparing Your Payment</h2>
          <div className="mt-4 mb-3">
            <CircularProgress />
          </div>
          <p css={{ fontSize: 18 }}>
            One moment while we open the secure payment page. Please don't close this window.
          </p>
        </>
      )}
    </div>
  )
}

export default PaymentRedirect
